import type { DerivedScheduleServiceEntry } from '../types/derivedSchedule.types';
import type { ConfiguredService } from '../types/service.types';
import { toLineDescription } from './lineDescription';
import type { Service } from '../types/services.types';

/** Maps derived-schedule entries to `ConfiguredService`; pricing comes from the catalog. */
export function mapDerivedServicesToConfigured(
    entries: DerivedScheduleServiceEntry[],
    getServiceById: (id: string) => Service | undefined
): ConfiguredService[] {
    // A date override pushed onto the next row can emit the same service twice
    // on one date; perturb the millisecond token for the nth row already carrying it.
    const occurrences = new Map<string, number>();

    return entries.map((entry) => {
        const catalogService = getServiceById(entry.serviceId);

        // quote-service validates `services.*.id` against `^<uuid>:[0-9]{13}$`; the
        // timestamp comes from `dueDate` so the id stays stable across re-derivation.
        const dueTimestamp = new Date(entry.dueDate).getTime();
        const baseId = `${entry.serviceId}:${dueTimestamp}`;
        const occurrence = occurrences.get(baseId) ?? 0;
        occurrences.set(baseId, occurrence + 1);
        const instanceId = occurrence === 0 ? baseId : `${entry.serviceId}:${dueTimestamp + occurrence}`;

        return {
            instanceId,
            name: catalogService?.name ?? entry.name,
            description: toLineDescription(catalogService?.description),
            price: catalogService?.price ?? 0,
            gross_price: catalogService?.gross_price ?? 0,
            startDate: entry.dueDate,
            interval: entry.intervalMonths ?? undefined,
            position: entry.position,
            serviceType: entry.serviceType,
            dueMileage: entry.dueMileage,
            costMode: catalogService?.cost_mode,
            items: catalogService?.items ?? [],
        };
    });
}
